import React from 'react';
import { 
  Target, 
  TrendingUp, 
  DollarSign, 
  FileText, 
  Users, 
  BarChart3,
  CheckCircle,
  Sparkles,
  Lock
} from 'lucide-react';

const features = [
  {
    icon: Target,
    title: "Personalized Career Plans",
    description: "AI-powered plans tailored to your specific role, experience, and career goals.",
    highlight: "Custom AI Plans",
    availableInFree: true
  },
  {
    icon: TrendingUp,
    title: "Skill-Building Exercises",
    description: "Interactive exercises and challenges designed for your industry and experience level.",
    highlight: "Interactive Learning",
    availableInFree: true
  },
  {
    icon: DollarSign,
    title: "Salary Insights by ZIP",
    description: "Real salary data for your position in your specific location to maximize earnings.",
    highlight: "Local Salary Data",
    availableInFree: false
  },
  {
    icon: FileText,
    title: "Resume Builder",
    description: "Professional resume templates and optimization tools for your industry.",
    highlight: "Industry Templates",
    availableInFree: false
  },
  {
    icon: BarChart3,
    title: "Progress Tracking",
    description: "Visual dashboards to monitor your career development and skill improvements.",
    highlight: "Real-time Analytics",
    availableInFree: true
  },
  {
    icon: Users,
    title: "Role-Specific Guidance",
    description: "Expert advice tailored to nurses, hairdressers, trades, business, and tech professionals.",
    highlight: "Expert Guidance",
    availableInFree: true
  }
];

export default function Features() {
  return (
    <section id="features" className="py-20 bg-background">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Powerful Features
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Everything you need for career success
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From personalized AI plans to local salary insights, we've got every aspect of your career growth covered.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features?.map((feature, index) => {
            const IconComponent = feature?.icon;
            return (
              <div key={index} className="group p-6 rounded-2xl border border-border hover:border-primary/20 hover:shadow-lg transition-all duration-300 bg-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    {IconComponent && <IconComponent className="w-6 h-6 text-primary" />}
                  </div>
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    {feature?.highlight}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {feature?.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature?.description}
                </p>
                <div className="mt-4 flex items-center text-sm font-medium">
                  {feature?.availableInFree ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2 text-success" />
                      <span className="text-success">Available in all plans</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2 text-primary" />
                      <span className="text-primary">Premium only</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-secondary/10 px-6 py-1 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Free tier includes basic features</span>
            </div>
            <div className="h-4 w-px bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="w-4 h-4 text-primary" />
              <span>Premium unlocks advanced tools</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}